import { supabase } from '../lib/supabase';

const calendarService = {
  async getCalendarEvents(userId, startDate, endDate) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: null };
      let query = supabase
        ?.from('calendar_events')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('start_time', { ascending: true });

      if (startDate) query = query?.gte('start_time', `${startDate}T00:00:00`);
      if (endDate) query = query?.lte('start_time', `${endDate}T23:59:59`);

      const { data, error } = await query;
      if (error) {
        console.warn('Error fetching calendar events:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getCalendarEvents:', err);
      return { data: [], error: err?.message };
    }
  },

  async getCallLogs(userId, limit = 50) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: null };
      const { data, error } = await supabase
        ?.from('call_logs')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);
      if (error) {
        console.warn('Error fetching call logs:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getCallLogs:', err);
      return { data: [], error: err?.message };
    }
  },

  async searchEventsWithNotes(userId, query) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: null };
      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.or(`title.ilike.%${query}%,notes.ilike.%${query}%,description.ilike.%${query}%`)
        ?.order('start_time', { ascending: false })
        ?.limit(20);
      if (error) {
        console.warn('Error searching events:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in searchEventsWithNotes:', err);
      return { data: [], error: err?.message };
    }
  },

  async createCalendarEvent(userId, eventData) {
    if (!userId) return { data: null, error: 'User ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.insert([{
          user_id: userId,
          title: eventData?.title || 'New Event',
          description: eventData?.description || '',
          notes: eventData?.notes || '',
          start_time: eventData?.start_time || new Date()?.toISOString(),
          end_time: eventData?.end_time || new Date(Date.now() + 3600000)?.toISOString(),
          event_type: eventData?.event_type || 'meeting',
          contact_id: eventData?.contact_id || null,
          location: eventData?.location || '',
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error creating calendar event:', err);
      return { data: null, error: err?.message };
    }
  },

  async updateCalendarEvent(eventId, updates) {
    if (!eventId) return { data: null, error: 'Event ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', eventId)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error updating calendar event:', err);
      return { data: null, error: err?.message };
    }
  },

  async deleteCalendarEvent(eventId) {
    if (!eventId) return { success: false, error: 'Event ID required' };
    try {
      if (!supabase) return { success: false, error: 'Database not available' };
      const { error } = await supabase
        ?.from('calendar_events')
        ?.delete()
        ?.eq('id', eventId);
      if (error) throw error;
      return { success: true, error: null };
    } catch (err) {
      console.error('Error deleting calendar event:', err);
      return { success: false, error: err?.message };
    }
  },

  async getContactNotes(userId, contactId) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: null };
      let query = supabase
        ?.from('calendar_events')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.not('notes', 'is', null)
        ?.order('created_at', { ascending: false })
        ?.limit(20);
      if (contactId) query = query?.eq('contact_id', contactId);
      const { data, error } = await query;
      if (error) {
        console.warn('Error fetching contact notes:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getContactNotes:', err);
      return { data: [], error: err?.message };
    }
  }
};

export default calendarService;